import { useQuery } from '@tanstack/react-query';
import { getWorkbooks } from './workbook-operations';
import { Project, ProjectQueryOptions } from './workbook-types';
import { useMemo } from 'react';

/**
 * Simplified workbook query hook using server-side filtering only
 */
export function useWorkbookQuery(options: ProjectQueryOptions = {}) {
  // Memoize the query key to prevent unnecessary re-renders
  const queryKey = useMemo(() => [
    'workbook-projects',
    options.filterByStatus || 'all',
    options.filterByTag || null,
    options.searchQuery || ''
  ], [options.filterByStatus, options.filterByTag, options.searchQuery]);

  return useQuery({
    queryKey,
    queryFn: () => getWorkbooks(options),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    // Prevent background refetches that can cause flickering
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Keep previous data to prevent flickering during transitions
    placeholderData: (previousData) => previousData,
    select: (fetchedData) => {
      const workbooks = fetchedData.workbooks || [];
      
      // Only apply client-side favorite sorting for 'all' status
      if (options.filterByStatus === 'all') {
        const sortedWorkbooks = [...workbooks].sort((a, b) => {
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          // For same favorite status, sort by display_order if available
          if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
            return a.displayOrder - b.displayOrder;
          }
          return 0;
        });
        return { projects: sortedWorkbooks, totalCount: fetchedData.totalCount || 0 };
      }
      
      return { projects: workbooks, totalCount: fetchedData.totalCount || 0 };
    },
  });
}
